import {
  CheckmarkOutline,
  CircleDash,
  Close,
  InProgress,
} from '@carbon/react/icons';
import type { StructuredPartyDetail, StructuredPartyListItem } from './types';
import { anchorCard, cardAddresses, isDeduplicatedResult } from './utils';

export interface PartyStatusConfig {
  label: string;
  icon: React.ElementType;
  className: string;
}

export const STATUS_CELL_CONFIG: Record<
  StructuredPartyListItem['status'],
  PartyStatusConfig
> = {
  not_started: {
    label: 'Not Started',
    icon: CircleDash,
    className: 'statusNotStarted',
  },
  in_progress: {
    label: 'In Progress',
    icon: InProgress,
    className: 'statusInProgress',
  },
  complete: {
    label: 'Complete',
    icon: CheckmarkOutline,
    className: 'statusComplete',
  },
  removed: {
    label: 'Removed',
    icon: Close,
    className: 'statusRemoved',
  },
};

export interface PartyState {
  isRemoved: boolean;
  isComplete: boolean;
  // Matched into another Imported entity (the Deduplicated Result), which is now
  // the party's current anchor; every match action operates on it.
  isProvisional: boolean;
  hasConfirmedMatch: boolean;
  // The Unresolvable warning stands in for an in-progress match's banner,
  // whose Add as New is withheld.
  showConfirmedMatchBanner: boolean;
  // The newest stage of the party's walk is a merge with no confirmed match — an
  // Entities-screen dedupe, or an in-document Reconcile that merged the anchor
  // away. Drives the "Resolved via" banner; canRevert decides Remove Correct Status.
  resolvedViaDedupe: boolean;
  // A reconcile is one atomic action reversed only by "Undo Reconcile";
  // reverting just the suggestion would split it in two and, for an alias,
  // strand the alias while sending the party back to Not Started.
  resolvedViaReconcile: boolean;
  isUserAdded: boolean;
  // The anchor dead-ends in a retired entity: only Remove Correct Status applies.
  isUnresolvable: boolean;
  // Read from the current anchor, the entity Add as New and Reconcile act on.
  hasUnresolvedAddressFlag: boolean;
  // Removed, complete or unresolvable: no further match actions (Change/Edit
  // Match, Mark as Correct, Re-run, Match Details, the no-match card's entry).
  matchLocked: boolean;
  // The server offers "Remove Correct Status" where RevertService reverses the
  // party's latest stage.
  canRevert: boolean;
  // The confirmation resource withholds destroy when another party made the
  // latest match on the anchor they share; Remove Correct Status is then offered
  // disabled with the server's reason.
  revertAllowed: boolean;
  revertDisabledReason: string | null;
  canRemove: boolean;
  canReconcile: boolean;
}

/**
 * Derives a party's status flags and which actions it allows.
 *
 * @param {StructuredPartyDetail} detail - The party's detail.
 * @return {PartyState} The derived state.
 */
export const partyState = (detail: StructuredPartyDetail): PartyState => {
  const isComplete = detail.status === 'complete';
  const isProvisional = detail.status === 'in_progress';
  const isRemoved = detail.status === 'removed';
  const isUnresolvable = detail.unresolvable ?? false;
  const hasConfirmedMatch = isComplete || isProvisional;
  const isUserAdded = detail.stagedEntity === null;
  const { resolvedViaDedupe, resolvedViaReconcile } = detail;
  const confirmation = detail.resources.confirmation;
  const revertAllowed = !confirmation || confirmation.auth.includes('destroy');
  return {
    isRemoved,
    isComplete,
    isProvisional,
    hasConfirmedMatch,
    showConfirmedMatchBanner:
      hasConfirmedMatch && !(isProvisional && isUnresolvable),
    resolvedViaDedupe,
    resolvedViaReconcile,
    isUserAdded,
    isUnresolvable,
    hasUnresolvedAddressFlag: cardAddresses(anchorCard(detail)).some(
      (a) => a.reviewFlag
    ),
    matchLocked: isRemoved || isComplete || isUnresolvable,
    canRevert: confirmation?.revertOffered ?? false,
    revertAllowed,
    revertDisabledReason: revertAllowed
      ? null
      : (confirmation?.disabledReason ?? null),
    canRemove: !isRemoved && (!hasConfirmedMatch || isUserAdded),
    canReconcile: !isRemoved && !isUserAdded && !isComplete && !isUnresolvable,
  };
};

/**
 * Why a party's addresses can't be moved from the document, or null when they
 * can. A party anchored on a Deduplicated Result shows that shared entity's
 * addresses, and an In Progress party's staged entity is already matched.
 *
 * @param {Pick<StructuredPartyListItem, 'status' | 'currentAnchor'>} party - The party to check.
 * @return {string | null} The reason, or null.
 */
export const moveAddressBlockedReason = (
  party: Pick<StructuredPartyListItem, 'status' | 'currentAnchor'>
): string | null =>
  isDeduplicatedResult(party) || party.status === 'in_progress'
    ? 'These addresses belong to a shared entity and can’t be moved from this document.'
    : null;

/**
 * Why a row action locked by a correct status is blocked, or null when it isn't locked.
 *
 * @param {boolean} locked - Whether the action is locked.
 * @param {string} verb - The action, as it reads after "To".
 * @return {string | null} The reason, or null.
 */
export const correctStatusLockReason = (
  locked: boolean,
  verb: string
): string | null => (locked ? `To ${verb}, remove correct status first` : null);

/**
 * Locks for the parties-overview row actions (clear flag, edit, move address,
 * remove). An In Progress party stays editable — its actions operate on the
 * current anchor — but, as on the party detail, it cannot be removed, and
 * moveAddressBlockedReason keeps its addresses from being moved.
 *
 * @param {Pick<StructuredPartyListItem, 'status' | 'unresolvable'>} party - The party row.
 * @return {{ locked: boolean, removeLocked: boolean }} Whether row actions, and removal, are locked.
 */
export const partyRowLock = (
  party: Pick<StructuredPartyListItem, 'status' | 'unresolvable'>
) => {
  const locked = party.status === 'complete' || !!party.unresolvable;
  return { locked, removeLocked: locked || party.status === 'in_progress' };
};
